import type { ISortedGroupedSchedule, ISortedShift } from '@/types/schedule';
import type { IBoundaryBlocks } from '@/api/types';
import type { IWeek } from '@/types/calendar';
import type { IWeekDay } from '@/types/calendar';

import { AllShiftItemType, CalendarDisplayRange, ShiftAssignStatus } from '@/store/enums';
import { BLOCKS_IN_HOUR } from '@/config/variables';
import { CachedBlock } from '@/classes/cachedBlock';
import { maxBy } from '@/helpers/other';

type IBaseShift = {
  scheduleId: number;
  itemData: {
    startBlock: number;
    endBlock: number;
    itemType: number;
    id: string;
  };
  displayBlocks: IBoundaryBlocks;
};

const _getCollisions = <T extends IBaseShift>(row: T[]) =>
  row.map(shift => ({
    id: shift.itemData.id,
    collideWith: row
      .filter(item => item.itemData.id !== shift.itemData.id && checkCollision(shift.displayBlocks, item.displayBlocks))
      .map(item => item.itemData.id),
  }));

const _alignShiftsToColumns = <T extends IBaseShift>(row: T[]): T[][] => {
  const newRow: T[][] = [];
  const collisions = _getCollisions(row);

  const isShiftAlreadyAdded = (shift: T) =>
    newRow.some(column => column.some(item => item.itemData.id === shift.itemData.id));

  row.forEach(shift => {
    if (newRow.length === 0) {
      newRow.push([shift]);
      return;
    }

    const shiftCollisions = collisions.find(item => item.id === shift.itemData.id)?.collideWith;
    newRow.forEach((column, columnIndex) => {
      if (isShiftAlreadyAdded(shift)) return;

      const isCollision = column.some(columnShift => shiftCollisions?.includes(columnShift.itemData.id));
      const hasNextColumn = newRow[columnIndex + 1];

      if (isCollision) {
        // if next column exists, skip iteration and continue parsing next column
        if (!hasNextColumn) newRow.push([shift]);
      } else {
        column.push(shift);
      }
    });
  });

  return newRow;
};

const _isTakenVto = (shift: ISortedShift): boolean =>
  (shift as ISortedGroupedSchedule).shifts?.some(shift => shift.shiftAssnStatus === ShiftAssignStatus.vtoAccepted);

const alignAllShifts = <T extends IBaseShift>(groupedArray: T[][]): T[][][] => groupedArray.map(_alignShiftsToColumns);

const groupAllShifts = <T extends IBaseShift>(shiftsArray: T[]): T[][] =>
  shiftsArray.reduce<T[][]>((acc, shift, index) => {
    if (index === 0) {
      acc.push([shift]);
      return acc;
    }
    const previousRow = acc[acc.length - 1];
    const {
      displayBlocks: { endBlock },
    } = maxBy(previousRow, obj => obj.displayBlocks.endBlock) as T;

    // get all shifts that start later, but end eariler than the longest shift from previous chunk
    // and include them to the previous chunk as well
    if (
      shift.displayBlocks.startBlock >= previousRow[0].displayBlocks.startBlock &&
      shift.displayBlocks.startBlock <= endBlock
    ) {
      const previousRow = acc[acc.length - 1];
      previousRow.push(shift);
      previousRow.sort((a, b) => a.scheduleId - b.scheduleId);
    } else {
      acc.push([shift]);
    }

    return acc;
  }, []);

const excludeIllegalShifts = (groupedShifts: ISortedShift[][]): ISortedShift[][] =>
  groupedShifts.reduce<ISortedShift[][]>((newGroups, group, idx, oldArray) => {
    // find client id of assigned shift (taken and not VTOed) if existing for current group
    const assignedShift = group.find(
      shift => shift.itemData.itemType === AllShiftItemType.schedule && !_isTakenVto(shift),
    );
    const { clientId: assignedClientId } = assignedShift || {};

    // if client is assigned skip
    if (assignedClientId) return [...newGroups, group];

    /**
     * This part is for available shifts
     */
    const filteredShifts = group.filter(availableShift => {
      // if shift is a taken VTO, always show it
      if (_isTakenVto(availableShift)) return true;

      const availableShiftStart = availableShift.displayBlocks.startBlock;
      const availableShiftEnd = availableShift.displayBlocks.endBlock;

      // find client id of assigned shift if existing for previous and following groups
      const prevAssignedGroup = oldArray[idx - 1]?.find(
        shift =>
          shift.itemData.itemType === AllShiftItemType.schedule &&
          shift.displayBlocks.endBlock === availableShiftStart - 1,
      );
      const prevAssignedClient = prevAssignedGroup?.clientId;
      const isPrevAssignedAcceptedVto = prevAssignedGroup && _isTakenVto(prevAssignedGroup);

      const nextAssignedGroup = oldArray[idx + 1]?.find(
        shift =>
          shift.itemData.itemType === AllShiftItemType.schedule &&
          shift.displayBlocks.startBlock === availableShiftEnd + 1,
      );
      const nextAssignedClient = nextAssignedGroup?.clientId;
      const isNextAssignedAcceptedVto = nextAssignedGroup && _isTakenVto(nextAssignedGroup);

      if (prevAssignedClient && availableShift.clientId !== prevAssignedClient && !isPrevAssignedAcceptedVto)
        return false;
      return !(nextAssignedClient && availableShift.clientId !== nextAssignedClient && !isNextAssignedAcceptedVto);
    });

    // If all shifts are illegal exclude group
    if (filteredShifts.length === 0) return newGroups;

    return [...newGroups, filteredShifts];
  }, []);

const transformDlsShift = <T extends { itemData: IBoundaryBlocks; displayBlocks: IBoundaryBlocks }>(
  shift: T,
  week: IWeek,
): T & { isShiftDlsBlocked?: boolean } => {
  if (!week.dst || week.dst.tzOffsetDiff > 0) return shift;

  // if shift starts before and ends in the DST change period, set its ending display block to the middle of DST period
  if (
    shift.itemData.startBlock < week.dst.startBlock &&
    shift.itemData.endBlock >= week.dst.startBlock &&
    shift.itemData.endBlock <= week.dst.endBlock
  ) {
    return {
      ...shift,
      displayBlocks: { ...shift.displayBlocks, endBlock: week.dst.startBlock },
      isShiftDlsBlocked: true,
    };
  }

  // if shift starts in the DST change period and ends after, set its starting display block to the middle of DST period
  if (
    shift.itemData.startBlock >= week.dst.startBlock &&
    shift.itemData.startBlock <= week.dst.endBlock &&
    shift.itemData.endBlock > week.dst.endBlock
  ) {
    return {
      ...shift,
      displayBlocks: { ...shift.displayBlocks, startBlock: week.dst.startBlock + 1 },
      isShiftDlsBlocked: true,
    };
  }

  return shift;
};

/**
 * This is business requirement, shifts from different clients cannot be picked up back to back. There needs to be at least one empty block between them.
 */
const checkIllegalShift = (shift1: ISortedShift, shift2: ISortedShift): boolean =>
  shift1.clientId !== shift2.clientId &&
  (shift2.originalStartBlock === (shift1.originalEndBlock ?? shift1.displayBlocks.endBlock) + 1 ||
    shift1.originalStartBlock === (shift2.originalEndBlock ?? shift2.displayBlocks.endBlock) + 1);

const checkCollision = (shift1: IBoundaryBlocks, shift2: IBoundaryBlocks): boolean =>
  (shift1.startBlock >= shift2.startBlock && shift1.startBlock <= shift2.endBlock) ||
  (shift1.endBlock >= shift2.startBlock && shift1.endBlock <= shift2.endBlock) ||
  (shift1.startBlock <= shift2.startBlock && shift1.endBlock >= shift2.endBlock);

const getDisplayBlocksForMultiDayView = (
  week: IWeek,
  tz: string,
  blocks: IBoundaryBlocks,
  selectedDaysRange: CalendarDisplayRange,
  day: IWeekDay,
): IBoundaryBlocks => {
  if (!day.isDlsDay) return blocks;
  const { startBlock: startBlockId, endBlock: endBlockId } = blocks;

  const startBlock = CachedBlock.fromBlockId(startBlockId, tz);
  const endBlock = CachedBlock.fromBlockId(endBlockId, tz);

  if (selectedDaysRange === CalendarDisplayRange.ONE_DAY || endBlock.timeZoneOffset === week.weekStartTotalOffset)
    return blocks;
  const tzOffsetDifference = (endBlock.timeZoneOffset - week.weekStartTotalOffset) * BLOCKS_IN_HOUR;

  if (startBlock.timeZoneOffset !== endBlock.timeZoneOffset)
    return {
      startBlock: startBlockId,
      endBlock: endBlockId + tzOffsetDifference,
    };
  return {
    startBlock: startBlockId + tzOffsetDifference,
    endBlock: endBlockId + tzOffsetDifference,
  };
};

export {
  groupAllShifts,
  alignAllShifts,
  excludeIllegalShifts,
  transformDlsShift,
  checkIllegalShift,
  checkCollision,
  getDisplayBlocksForMultiDayView,
};
